import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import './MyOrders.css';
import Button from '../../components/UI/Button/Button';
import Breadcrumb from "../../components/Breadcrumb/Breadcrumb";
import AuthContext from '../../context/AuthContext';
import LanguageContext from '../../context/LanguageContext.js';
import { useToast } from '../../context/ToastContext';
import { getUserOrders, cancelUserOrder } from '../../api/orderApi';
import CustomDropdown from '../../components/UI/CustomDropdown/CustomDropdown';
import translationMenu from '../../data/translationMenu';
import { usePublicSettings } from '../../context/PublicSettingsContext';
import { formatOrderMoney } from '../../utils/currencyFormat';

// ZATCA tax invoice page (served by the API); the backend link when it knows its public URL.
const invoiceLink = (invoice) =>
    invoice.url || `${process.env.REACT_APP_API_BASE_URL}/api/public/invoices/${invoice.publicToken}`;

const MyOrders = () => {
    const { dir } = useContext(LanguageContext);
    const { t, i18n } = useTranslation();
    const currentLanguage = i18n.language;
    const { currencyCode, storeLocale } = usePublicSettings();
    const moneyLocaleOpts = { locale: storeLocale };
    const { user } = useContext(AuthContext);
    const { addToast } = useToast();
    const showTrackOrdersLinks = (process.env.REACT_APP_SHOW_TRACK_ORDERS_LINKS || 'false').toLowerCase() === 'true';

    const [orders, setOrders] = useState([]);
    const [filteredOrders, setFilteredOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [statusFilter, setStatusFilter] = useState('all');
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [cancellingOrderId, setCancellingOrderId] = useState(null);

    // Status filter options
    const statusOptions = [
        { value: 'all', label: t('myOrders.allOrders') },
        { value: 'pending', label: t('myOrders.statusPending') },
        { value: 'confirmed', label: t('myOrders.statusConfirmed') },
        { value: 'preparing', label: t('myOrders.statusPreparing') },
        { value: 'ready', label: t('myOrders.statusReady') },
        { value: 'delivered', label: t('myOrders.statusDelivered') },
        { value: 'completed', label: t('myOrders.statusCompleted') },
        { value: 'cancelled', label: t('myOrders.statusCancelled') },
    ];

    // Load user orders
    useEffect(() => {
        const loadOrders = async () => {
            if (!user || !user._id || user._id === '') {
                setError(t('myOrders.loginRequired'));
                setLoading(false);
                return;
            }

            try {
                setLoading(true);
                setError(null);

                const response = await getUserOrders();
                
                if (response.status === 'success') {
                    setOrders(response.data);
                    setFilteredOrders(response.data);
                } else {
                    setError(response.message || t('myOrders.loadError'));
                }
            } catch (err) {
                setError(err?.message || err?.response?.data?.message || t('myOrders.loadError'));
            } finally {
                setLoading(false);
            }
        };

        loadOrders();
    }, [user, t]);

    // Cancel order function
    const handleCancelOrder = async (orderId) => {
        if (!window.confirm(t('myOrders.confirmCancel'))) {
            return;
        }

        try {
            setCancellingOrderId(orderId);
            const response = await cancelUserOrder(orderId);
            
            if (response.status === 'success') {
                // Update the order status in the local state
                setOrders(prevOrders => 
                    prevOrders.map(order => 
                        order._id === orderId 
                            ? { ...order, orderStatus: 'cancelled', cancelledAt: new Date() }
                            : order
                    )
                );
                setFilteredOrders(prevOrders => 
                    prevOrders.map(order => 
                        order._id === orderId 
                            ? { ...order, orderStatus: 'cancelled', cancelledAt: new Date() }
                            : order
                    )
                );
                
                // Close details if this order was selected
                if (selectedOrder && selectedOrder._id === orderId) {
                    setSelectedOrder(null);
                }
                
                addToast(t('myOrders.cancelSuccess'), 'success');
            } else {
                addToast(response.message || t('myOrders.cancelError'), 'error');
            }
        } catch (err) {
            addToast(err?.message || err?.response?.data?.message || t('myOrders.cancelError'), 'error');
        } finally {
            setCancellingOrderId(null);
        }
    };

    // Check if order can be cancelled
    const canCancelOrder = (orderStatus) => {
        return ['pending', 'confirmed'].includes(orderStatus);
    };

    // Filter orders by status
    useEffect(() => {
        if (statusFilter === 'all') {
            setFilteredOrders(orders);
        } else {
            setFilteredOrders(orders.filter(order => order.orderStatus === statusFilter));
        }
    }, [orders, statusFilter]);

    const getStatusColor = (status) => {
        switch (status?.toLowerCase()) {
            case 'pending': return 'status-pending';
            case 'confirmed': return 'status-confirmed';
            case 'preparing': return 'status-preparing';
            case 'ready': return 'status-ready';
            case 'delivered': return 'status-delivered';
            case 'completed': return 'status-completed';
            case 'cancelled': return 'status-cancelled';
            default: return 'status-pending';
        }
    };

    const getStatusText = (status) => {
        switch (status?.toLowerCase()) {
            case 'awaiting-payment': return t('myOrders.statusAwaitingPayment');
            case 'pending': return t('myOrders.statusPending');
            case 'confirmed': return t('myOrders.statusConfirmed');
            case 'preparing': return t('myOrders.statusPreparing');
            case 'ready': return t('myOrders.statusReady');
            case 'delivered': return t('myOrders.statusDelivered');
            case 'completed': return t('myOrders.statusCompleted');
            case 'cancelled': return t('myOrders.statusCancelled');
            default: return status;
        }
    };

    if (!user) {
        return (
            <main className="main">
                <Breadcrumb title={t('myOrders.title')} />
                <section className="my-orders-page">
                    <div className="container my-orders-body" dir={dir()}>
                        <div className="my-orders-login-card">
                            <h2 className="my-orders-login-title">{t('myOrders.loginRequired')}</h2>
                            <p className="my-orders-login-text">{t('myOrders.loginMessage')}</p>
                            <Button to={`/${currentLanguage}/sign-in`} flex>
                                {t('myOrders.signIn')}
                            </Button>
                        </div>
                    </div>
                </section>
            </main>
        );
    }

    return (
        <main className="main">
            <Breadcrumb title={t('myOrders.title')} />

            <section className="my-orders-page">
                <div className="container my-orders-body" dir={dir()}>
                    <div className="my-orders-filter">
                        <span className="my-orders-filter-label">{t('myOrders.filterByStatus')}</span>
                        <CustomDropdown
                            options={statusOptions}
                            value={statusFilter}
                            onChange={setStatusFilter}
                            placeholder={t('myOrders.allOrders')}
                        />
                    </div>

                    {error && (
                        <div className="my-orders-error" role="alert">
                            <p>{error}</p>
                        </div>
                    )}

                    {loading ? (
                        <div className="my-orders-loading">
                            <div className="my-orders-spinner" aria-hidden />
                            <p>{t('myOrders.loading')}</p>
                        </div>
                    ) : filteredOrders.length === 0 ? (
                        <div className="my-orders-empty">
                            <h2 className="my-orders-empty-title">{t('myOrders.noOrders')}</h2>
                            <p className="my-orders-empty-text">{t('myOrders.noOrdersMessage')}</p>
                            <Button to={`/${currentLanguage}/menu`} flex>
                                {t('myOrders.startOrdering')}
                            </Button>
                        </div>
                    ) : (
                        <div className="my-orders-grid">
                            {filteredOrders.map((order) => (
                                <article key={order._id} className="my-orders-card">
                                    <div className="my-orders-card-head">
                                        <div className="my-orders-card-number">#{order.orderNumber}</div>
                                        <div className={`my-orders-status ${getStatusColor(order.orderStatus)}`}>
                                            {getStatusText(order.orderStatus)}
                                        </div>
                                    </div>

                                    <div className="my-orders-card-meta">
                                        <div className="my-orders-meta-block">
                                            <span className="my-orders-meta-label">{t('myOrders.orderDate')}</span>
                                            <span className="my-orders-meta-value">
                                                {new Date(order.createdAt).toLocaleDateString(currentLanguage)}
                                            </span>
                                        </div>
                                        <div className="my-orders-meta-block">
                                            <span className="my-orders-meta-label">{t('myOrders.total')}</span>
                                            <span className="my-orders-meta-value my-orders-meta-value--accent">{formatOrderMoney(order.totalAmount, order.currency, currencyCode, moneyLocaleOpts)}</span>
                                        </div>
                                    </div>

                                    <div className="my-orders-preview">
                                        <h3 className="my-orders-preview-title">{t('myOrders.items')}</h3>
                                        <div className="my-orders-preview-list">
                                            {order.items?.slice(0, 3).map((item, idx) => (
                                                <div key={idx} className="my-orders-preview-row">
                                                    <span className="my-orders-preview-name">
                                                        {item.menuItemId?.name?.[currentLanguage] ||
                                                         item.menuItemId?.name?.en ||
                                                         item.name?.[currentLanguage] ||
                                                         item.name?.en ||
                                                         'Unknown Item'}
                                                    </span>
                                                    <span className="my-orders-preview-qty">×{item.quantity}</span>
                                                </div>
                                            ))}
                                            {order.items?.length > 3 && (
                                                <div className="my-orders-preview-more">
                                                    +{order.items.length - 3} {t('myOrders.moreItems')}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <div className="my-orders-card-actions">
                                        <Button
                                            variant="outline"
                                            type="button"
                                            onClick={() => setSelectedOrder(selectedOrder?._id === order._id ? null : order)}
                                        >
                                            {selectedOrder?._id === order._id ? t('myOrders.hideDetails') : t('myOrders.viewDetails')}
                                        </Button>
                                        {canCancelOrder(order.orderStatus) && (
                                            <Button
                                                variant="danger"
                                                type="button"
                                                onClick={() => handleCancelOrder(order._id)}
                                                disabled={cancellingOrderId === order._id}
                                            >
                                                {cancellingOrderId === order._id ? t('myOrders.cancelling') : t('myOrders.cancelOrder')}
                                            </Button>
                                        )}
                                        {showTrackOrdersLinks && (
                                            <Button
                                                to={`/${currentLanguage}/track-order?orderNumber=${order.orderNumber}`}
                                                flex
                                            >
                                                {t('myOrders.trackOrder')}
                                            </Button>
                                        )}
                                        {order.invoice && (
                                            <Button
                                                variant="outline"
                                                href={invoiceLink(order.invoice)}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                flex
                                            >
                                                {t('myOrders.viewInvoice')}
                                            </Button>
                                        )}
                                    </div>

                                    {selectedOrder?._id === order._id && (
                                        <div className="my-orders-detail">
                                            <div className="my-orders-detail-section">
                                                <h3 className="my-orders-detail-heading">{t('myOrders.orderSummary')}</h3>
                                                <div className="my-orders-summary-grid">
                                                    <div className="my-orders-summary-cell">
                                                        <span className="my-orders-summary-label">{t('myOrders.orderType')}</span>
                                                        <span className="my-orders-summary-value">{order.customerInfo?.orderType || 'N/A'}</span>
                                                    </div>
                                                    <div className="my-orders-summary-cell">
                                                        <span className="my-orders-summary-label">{t('myOrders.paymentMethod')}</span>
                                                        <span className="my-orders-summary-value">{order.paymentMethod || 'N/A'}</span>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="my-orders-detail-section my-orders-detail-items">
                                                <h3 className="my-orders-detail-heading">{t('myOrders.allItems')}</h3>
                                                {order.items?.map((item, idx) => (
                                                    <div key={idx} className="my-orders-line">
                                                        <div className="my-orders-line-info">
                                                            {item.menuItemId?.imageUrl && (
                                                                <img
                                                                    src={item.menuItemId.imageUrl}
                                                                    alt={item.menuItemId.name?.[currentLanguage] || item.menuItemId.name?.en}
                                                                    className="my-orders-line-img"
                                                                />
                                                            )}
                                                            <div>
                                                                <div className="my-orders-line-name">
                                                                    {item.menuItemId?.name?.[currentLanguage] ||
                                                                     item.menuItemId?.name?.en ||
                                                                     item.name?.[currentLanguage] ||
                                                                     item.name?.en ||
                                                                     'Unknown Item'}
                                                                </div>
                                                                {item.menuItemId?.category && (
                                                                    <div className="my-orders-line-cat">
                                                                        {translationMenu[currentLanguage]?.categories?.[item.menuItemId.category] || item.menuItemId.category}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                        <div className="my-orders-line-price">
                                                            <span className="my-orders-line-qty">×{item.quantity}</span>
                                                            <span className="my-orders-line-amount">{formatOrderMoney(item.price, order.currency, currencyCode, moneyLocaleOpts)}</span>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>

                                            {order.customerInfo?.deliveryAddress && order.customerInfo.orderType === 'delivery' && (
                                                <div className="my-orders-detail-section">
                                                    <h3 className="my-orders-detail-heading">{t('myOrders.deliveryAddress')}</h3>
                                                    <div className="my-orders-address">
                                                        {order.customerInfo.deliveryAddress.street}
                                                        {order.customerInfo.deliveryAddress.apartment && `, ${order.customerInfo.deliveryAddress.apartment}`}
                                                        <br />
                                                        {order.customerInfo.deliveryAddress.city}, {order.customerInfo.deliveryAddress.postalCode}
                                                        <br />
                                                        {order.customerInfo.deliveryAddress.country}
                                                    </div>
                                                </div>
                                            )}

                                            {order.customerNotes && (
                                                <div className="my-orders-detail-section">
                                                    <h3 className="my-orders-detail-heading">{t('myOrders.specialInstructions')}</h3>
                                                    <p className="my-orders-notes">{order.customerNotes}</p>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </article>
                            ))}
                        </div>
                    )}

                    <div className="my-orders-footer-actions">
                        <Button to={`/${currentLanguage}/menu`} flex>
                            {t('myOrders.continueShopping')}
                        </Button>
                        {showTrackOrdersLinks && (
                            <Button to={`/${currentLanguage}/track-order`} variant="outline">
                                {t('myOrders.trackOrder')}
                            </Button>
                        )}
                    </div>
                </div>
            </section>
        </main>
    );
};

export default MyOrders;